import ZtServiceFactory from '../zt';
import reviewContent from './review-desc.json';

const INVALID_REVIEW_STATUS_MESSAGE = 'invalid review status';

// 2026-09-22 业务变更：数值 0 与空值同等视作"无数据"——零值不再按有效数据渲染动态句，
// 改走各条目的 fallback 默认文案（如通关 0 次展示"各副本中，你过五关斩六将"）；
// 未配置 fallback 的条目沿用既有缺失规则隐藏整句。字符串 "0" 不受影响（仅数值 0 命中）。
function isMissing(value) {
  return (
    value === 0 || value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)
  );
}

// 部分字段用特定返回值表达"无有效数据"（如家族职位返回"成员"表示从未担任过职位），
// 命中 review-desc.json 中该项 treatedAsMissingValues 声明的等值列表时，按缺失处理走 fallback。
function matchesTreatedAsMissingValues(value, treatedAsMissingValues) {
  if (!Array.isArray(treatedAsMissingValues) || treatedAsMissingValues.length === 0) {
    return false;
  }
  const normalizedValue = String(value).trim();
  return treatedAsMissingValues.some(candidate => String(candidate).trim() === normalizedValue);
}

function formatInteger(value) {
  const text = String(value);
  const match = text.match(/^(-?)(\d+)$/);
  if (!match) {
    return text;
  }
  return `${match[1]}${match[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function formatDate(value) {
  const match = String(value).match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) {
    return String(value);
  }
  return `${match[1]}年${Number(match[2])}月${Number(match[3])}日`;
}

function formatField(value, formatter) {
  if (formatter === 'integer_with_thousands_separator') {
    return formatInteger(value);
  }
  if (formatter === 'YYYY年M月D日') {
    return formatDate(value);
  }
  // 列表类字段（家族职位英文逗号分隔、弟子数组 String 化）统一展示为中文顿号（2026-09-23 CSV 更新）
  if (formatter === 'comma_to_enumeration') {
    return String(value).replace(/,/g, '、');
  }
  return String(value);
}

/**
 * 分组内标签项 → 标签名：2026-09-21 实测组内为 { name, desc } 对象（desc 为达成条件描述，
 * 本模块契约不含 desc，仅取 name 保持 string[] 输出）；兼容旧字符串形态；非法项过滤。
 */
function pickTagName(item) {
  if (typeof item === 'string') {
    return item.trim() === '' ? '' : item;
  }

  if (item && typeof item === 'object' && typeof item.name === 'string' && item.name.trim() !== '') {
    return item.name;
  }

  return '';
}

/**
 * 摊平按稀有度分组的 tag_list（键 "3"/"2"/"1"，3 为最高稀有度，组内为 { name, desc } 对象
 * ——2026-09-21 实测）为降序标签数组（组内保持接口顺序），页面默认取前三个即高稀有度优先；
 * 兼容旧字符串数组形态（顺序原样）。
 *
 * @param {object|string[]|undefined} rawTagList get_review.review_data.tag_list 原始值。
 * @returns {string[]} 降序标签数组。
 */
function flattenTagListByRarity(rawTagList) {
  if (Array.isArray(rawTagList)) {
    return rawTagList.filter(value => typeof value === 'string' && value.trim() !== '');
  }

  if (rawTagList && typeof rawTagList === 'object') {
    return ['3', '2', '1'].reduce(
      (list, rarity) => list.concat(Array.isArray(rawTagList[rarity]) ? rawTagList[rarity].map(pickTagName).filter(name => name !== '') : []),
      []
    );
  }

  return [];
}

/** Preserves each summary tag's real rarity while keeping the existing string-array contract. */
function createTagRarityMap(rawTagList) {
  if (Array.isArray(rawTagList)) {
    return rawTagList.reduce((map, tag) => {
      if (typeof tag === 'string' && tag.trim() !== '') map[tag] = 1;
      return map;
    }, {});
  }
  if (!rawTagList || typeof rawTagList !== 'object') return {};
  return ['1', '2', '3'].reduce((map, rarity) => {
    (Array.isArray(rawTagList[rarity]) ? rawTagList[rarity] : []).forEach(item => {
      const tag = pickTagName(item);
      if (tag !== '') map[tag] = Number(rarity);
    });
    return map;
  }, {});
}

function createTextLine(id, text) {
  return {
    id,
    segments: [{ type: 'text', text }]
  };
}

function createDynamicSegments(item, reviewData) {
  const fieldNames = item.apiFields || [];
  const allowedFields = fieldNames.reduce((result, field) => {
    result[field] = true;
    return result;
  }, {});
  const segments = [];
  const tokenPattern = /{{([^}]+)}}/g;
  let lastIndex = 0;
  let match = tokenPattern.exec(item.template);

  while (match) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', text: item.template.slice(lastIndex, match.index) });
    }
    const field = match[1];
    if (allowedFields[field]) {
      segments.push({
        type: 'dynamic',
        field,
        text: formatField(reviewData[field], item.formatters && item.formatters[field])
      });
    }
    lastIndex = match.index + match[0].length;
    match = tokenPattern.exec(item.template);
  }

  if (lastIndex < item.template.length) {
    segments.push({ type: 'text', text: item.template.slice(lastIndex) });
  }
  return segments;
}

function createItemLine(item, reviewData) {
  if (item.kind === 'fixed') {
    return createTextLine(item.id, item.template);
  }

  // apiFields is the formal contract allowlist; unresolved template placeholders must never become response reads.
  const fields = item.apiFields || [];
  const hasMissingField =
    fields.length === 0 ||
    fields.some(
      field =>
        isMissing(reviewData[field]) || matchesTreatedAsMissingValues(reviewData[field], item.treatedAsMissingValues && item.treatedAsMissingValues[field])
    );
  if (hasMissingField) {
    if (item.fallback && item.fallback.mode === 'replace') {
      return createTextLine(item.id, item.fallback.text);
    }
    return null;
  }

  return {
    id: item.id,
    segments: createDynamicSegments(item, reviewData)
  };
}

function createSection(section, reviewData) {
  if (section.sectionFallback && section.id === 'family' && isMissing(reviewData.tong_id)) {
    return {
      id: section.id,
      title: section.title,
      lines: section.sectionFallback.lines.map((text, index) => createTextLine(`section-fallback-${index}`, text))
    };
  }

  return {
    id: section.id,
    title: section.title,
    lines: section.items.map(item => createItemLine(item, reviewData)).filter(Boolean)
  };
}

// 单页最大条数默认值（2026-09-22 需求）：章节内容超出内容区 847px 固定高度会被 overflow hidden 裁掉，
// 超长章节按条数切片拆页。各章节可在 review-desc.json 的 section.maxLinesPerPage 单独配置（如深刻记忆 5 → 7 条拆 5+2），
// 未配置或配置非法时回落本默认值；页码总数随 sections 长度自动变化。
const DEFAULT_MAX_LINES_PER_PAGE = 5;

/** 超长章节按条数上限拆成连续多页：同 id/title（锚点样式与章节标题共用），key 唯一供 React 列表渲染。 */
function splitSection(section, maxLinesPerPage) {
  const limit = Number.isInteger(maxLinesPerPage) && maxLinesPerPage > 0 ? maxLinesPerPage : DEFAULT_MAX_LINES_PER_PAGE;
  const pages = [];
  for (let index = 0; index < section.lines.length; index += limit) {
    pages.push({
      id: section.id,
      key: index === 0 ? section.id : `${section.id}--p${Math.floor(index / limit) + 1}`,
      title: section.title,
      lines: section.lines.slice(index, index + limit)
    });
  }
  return pages;
}

/**
 * Convert formal get_review fields and the reviewed content document into render-ready sections.
 *
 * @param {object} reviewData Formal get_review.review_data payload.
 * @returns {Array<{id:string,title:string,lines:Array}>} Ordered review sections for the static stage shell.
 */
export function createReviewViewModel(reviewData = {}) {
  // 拆页发生在 createSection（fallback 全部应用）之后：lines 即最终渲染内容；
  // 单页条数上限取各 section 的 maxLinesPerPage 配置，未配置回落默认值
  return reviewContent.sections
    .map(section => splitSection(createSection(section, reviewData), section.maxLinesPerPage))
    .reduce((result, pages) => result.concat(pages), []);
}

/**
 * Convert formal get_review fields into the stable summary contract.
 *
 * @param {object} reviewData Formal get_review.review_data payload.
 * @returns {object} Summary text and filtered tags for the summary shell.
 */
export function createReviewSummaryViewModel(reviewData = {}) {
  const identityParts = [
    reviewData.role_menpai_name
    // , reviewData.role_gender
  ]
    .filter(value => !isMissing(value))
    .map(String);
  const tags = flattenTagListByRarity(reviewData.tag_list);
  const tagRarities = createTagRarityMap(reviewData.tag_list);
  return {
    formerRoleNameText: isMissing(reviewData.role_name) ? '' : String(reviewData.role_name),
    jianxiaScoreText: isMissing(reviewData.jianxia_score) ? '' : formatInteger(reviewData.jianxia_score),
    firstLoginDateText: isMissing(reviewData.first_login_date) ? '' : formatDate(reviewData.first_login_date),
    roleIdentityText: identityParts.join(' · '),
    tags,
    tagRarities
  };
}

// get_review 后端带处理锁：上一次响应到达前再次请求会报 -10201「系统处理中」。
// 错误后的立即重试、登录态晚就绪引发的重复 initialize 都可能在上一次 get_review
// 未返回时再次发起，这里把飞行中的请求模块级共享，并发调用复用同一次请求。
// 生命周期跟随 get_review 本身而非 loadReviewContext 的整体 Promise.all，
// get_config 先失败也不会提前释放；登录态由 null 写入到具体账号属同一用户会话，
// 真实换号的间隔远大于请求飞行窗口，不存在跨用户串数据。
let inflightReviewPromise = null;

function shareInflightReview(reviewProvider) {
  if (!inflightReviewPromise) {
    // 与原实现一致：请求在调用时同步发起，仅吞掉 provider 的同步 throw
    let request;
    try {
      request = Promise.resolve(reviewProvider(undefined));
    } catch (error) {
      request = Promise.reject(error);
    }
    inflightReviewPromise = request.then(
      value => {
        inflightReviewPromise = null;
        return value;
      },
      error => {
        inflightReviewPromise = null;
        throw error;
      }
    );
  }
  return inflightReviewPromise;
}

/**
 * Load review state and data through the generated ZT service, then hide transport envelopes from UI consumers.
 *
 * @param {object} options Optional providers used by isolated tests.
 * @param {Function} options.configProvider Provider matching getConfig(account).
 * @param {Function} options.reviewProvider Provider matching getReview(account).
 * @returns {Promise<{reviewStatus:number,sections:Array,summary:object}>} Stable page review context.
 */
export async function loadReviewContext(options = {}) {
  let service;
  if (!options.configProvider || !options.reviewProvider) {
    service = ZtServiceFactory.getDefaultInstance();
  }
  const configProvider = options.configProvider || service.getConfig.bind(service);
  const reviewProvider = options.reviewProvider || service.getReview.bind(service);
  const [config, review] = await Promise.all([configProvider(undefined), shareInflightReview(reviewProvider)]);
  //const [config, review] = await Promise.all([configProvider(undefined), reviewProvider(undefined)]);

  if (!config || (config.review_status !== 0 && config.review_status !== 1)) {
    throw new Error(INVALID_REVIEW_STATUS_MESSAGE);
  }

  const reviewData = review && review.review_data ? review.review_data : {};
  return {
    reviewStatus: config.review_status,
    sections: createReviewViewModel(reviewData),
    summary: createReviewSummaryViewModel(reviewData)
  };
}

/**
 * Mark the first review as complete without exposing transport metadata as page state.
 *
 * @param {object} options Optional provider used by isolated tests.
 * @param {Function} options.provider Provider matching finishReview(account).
 * @returns {Promise<void>} Resolves only after the backend completion gate succeeds.
 */
export async function finishReview(options = {}) {
  const service = options.provider ? null : ZtServiceFactory.getDefaultInstance();
  const provider = options.provider || service.finishReview.bind(service);
  await provider(undefined);
}

export default {
  loadReviewContext,
  finishReview,
  createReviewViewModel,
  createReviewSummaryViewModel
};
